import { useSyncExternalStore } from "react";
import { type AudioLike, createSounds } from "./engine";

const STORAGE_KEY = "hcc:volume";

/** The one sound system of the page, on the browser's ports; a missing piece stays quiet. */
export const sounds = createSounds({
  audio: () => {
    const Ctor = window.AudioContext;
    return Ctor ? (new Ctor() as unknown as AudioLike) : null;
  },
  load: (from) =>
    fetch(from)
      .then((res) => (res.ok ? res.arrayBuffer() : null))
      .catch(() => null),
  visible: () => document.visibilityState === "visible",
  // In Discord the table is an iframe: another window, or Discord's own chat, takes the focus.
  attentive: () => document.visibilityState === "visible" && document.hasFocus(),
  now: () => Date.now(),
  readVolume: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === null ? null : Number(stored);
    } catch {
      return null;
    }
  },
  writeVolume: (volume) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(volume));
    } catch {
      // Without storage the choice lasts this session only.
    }
  },
});

/** What a browser counts as a gesture: on a phone the end of a touch, not its start (review 0027 C8). */
const GESTURES = ["pointerup", "click", "keydown"] as const;

/**
 * A browser allows no sound before a gesture. Every gesture primes again rather than only the
 * first: the first may come before the page may make sound, and a phone call suspends it later.
 * Returns the cleanup.
 */
export function unlockOnGesture(): () => void {
  const unlock = () => sounds.prime();
  for (const gesture of GESTURES) window.addEventListener(gesture, unlock, { passive: true });
  return () => {
    for (const gesture of GESTURES) window.removeEventListener(gesture, unlock);
  };
}

export function useVolume(): number {
  return useSyncExternalStore(sounds.subscribe, sounds.volume, sounds.volume);
}
